import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { logger } from "@/lib/logger";

/**
 * Tiny SVG sparkline of the last N daily prices for a single investment.
 * Fetches lazily on mount; renders nothing until data arrives.
 */
export default function PriceSparkline({ investmentId, days = 30, width = 96, height = 26, positive }) {
  const [points, setPoints] = useState(null);

  useEffect(() => {
    let cancelled = false;
    api.get(`/investments/${investmentId}/price-history`, { params: { days } })
      .then((r) => { if (!cancelled) setPoints(r.data?.points || []); })
      .catch((e) => logger.warn("sparkline history", e));
    return () => { cancelled = true; };
  }, [investmentId, days]);

  if (!points || points.length < 2) return null;

  const values = points.map((p) => Number(p.price) || 0);
  const min = Math.min(...values);
  const max = Math.max(...values);
  const range = Math.max(0.0001, max - min);
  const stepX = width / (values.length - 1);
  const coords = values.map((v, i) => {
    const x = i * stepX;
    const y = height - ((v - min) / range) * (height - 4) - 2;
    return `${x.toFixed(1)},${y.toFixed(1)}`;
  });
  const stroke = positive ? "#1C7C54" : "#D95D39";
  const last = values[values.length - 1];
  const first = values[0];
  const trendUp = last >= first;
  const color = positive == null ? (trendUp ? "#1C7C54" : "#D95D39") : stroke;

  return (
    <svg
      width={width}
      height={height}
      viewBox={`0 0 ${width} ${height}`}
      data-testid={`sparkline-${investmentId}`}
      className="opacity-80"
    >
      <polyline
        points={coords.join(" ")}
        fill="none"
        stroke={color}
        strokeWidth="1.5"
        strokeLinejoin="round"
        strokeLinecap="round"
      />
    </svg>
  );
}
